import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { cx } from '../internal/cx';
import { IconButton } from './IconButton';

/** How wide the dialog may grow. */
export type DialogSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Readonly<Record<DialogSize, string>> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

/** Props of {@link Dialog}. */
export interface DialogProps {
  /** Whether the dialog is shown. Always controlled: the opener owns the state. */
  open: boolean;
  /** Called with `false` when the reader closes the dialog (Escape, the backdrop, the close button). */
  onOpenChange: (open: boolean) => void;
  /** Heading, in the reader's language. Required: a dialog without a name is unusable by screen readers. */
  title: string;
  /**
   * One sentence saying what the dialog is for. Required for the same reason; pass
   * `hideDescription` when the body already makes it obvious to a sighted reader.
   */
  description: string;
  /** Keeps the description for assistive technology but takes it out of the layout. */
  hideDescription?: boolean;
  /** Accessible name of the close button, e.g. "Kapat". */
  closeLabel: string;
  /** Maximum width; `md` by default. */
  size?: DialogSize;
  /** Body of the dialog. */
  children?: ReactNode;
  /** Action row, usually two `<Button>`s. Rendered right-aligned under the body. */
  footer?: ReactNode;
  /** Extra utility classes for the dialog panel. */
  className?: string;
}

/**
 * A modal dialog: focus is trapped, the page behind it is inert, Escape closes it.
 *
 * @example
 * <Dialog
 *   open={confirming}
 *   onOpenChange={setConfirming}
 *   title={t('mail.deleteTitle')}
 *   description={t('mail.deleteDescription', { count })}
 *   closeLabel={t('common.close')}
 *   footer={<><Button onClick={cancel}>{t('common.cancel')}</Button>
 *            <Button variant="danger" onClick={remove}>{t('common.delete')}</Button></>}
 * />
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  hideDescription = false,
  closeLabel,
  size = 'md',
  children,
  footer,
  className,
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="ui-animate-overlay fixed inset-0 z-40 bg-overlay" />
        <DialogPrimitive.Content
          className={cx(
            'ui-animate-pop fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)]',
            '-translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-lg border border-border',
            'bg-surface-elevated p-4 text-text shadow-lg outline-none',
            SIZE_CLASSES[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <DialogPrimitive.Title className="text-title3 text-text">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <IconButton icon="close" label={closeLabel} size="sm" className="-mt-1 -mr-1" />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description
            className={cx(hideDescription ? 'sr-only' : 'text-subhead text-text-secondary')}
          >
            {description}
          </DialogPrimitive.Description>
          {children !== undefined && (
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          )}
          {footer !== undefined && (
            <div className="flex flex-wrap justify-end gap-2 pt-1">{footer}</div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
